<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <p class="mb-0 main-identifier fw-semibold">Reports</p>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">View Approval Authorities</p>
    </div>

    <div class="d-flex justify-content-center">
      <div class="form-outer" style="max-width: 1400px;">
        <div class="view-item position-relative">

          <div class="form-inner">
            <div class="form-header d-flex justify-content-between align-items-center report">
              <h1 class="mb-0">Approval Authorities</h1>
            </div>

            <div class="form-body">
              <!-- Table Controls -->
              <div v-if="!loading" class="table-controls">
                <div class="table-controls-top">
                  <div class="table-controls-left">
                    <div class="entries-control">
                      <span>Show</span>
                      <select class="form-select form-select-sm" v-model.number="pageLength">
                        <option :value="10">10</option>
                        <option :value="25">25</option>
                        <option :value="50">50</option>
                        <option :value="100">100</option>
                      </select>
                      <span>Entries</span>
                    </div>

                    <div class="search-box">
                      <span class="material-symbols-outlined search-icon">search</span>
                      <input type="search" v-model="search" class="form-control form-control-sm search-input"
                        placeholder="Search approval authorities..." />
                    </div>
                  </div>

                  <div class="export-links">
                    <a href="#" class="export-link text-decoration-none" @click.prevent="exportToPDF()">Export to
                      PDF</a>
                    <a href="#" class="export-link text-decoration-none" @click.prevent="exportToExcel()">Export to
                      Excel</a>
                  </div>
                </div>
              </div>

              <!-- Table -->
              <div v-if="loading" class="loading-state text-center py-5">
                <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
                <p class="mt-2 mb-0 text-muted">Loading approval authorities...</p>
              </div>

              <div v-else class="table-wrapper">
                <div class="table-container">
                  <table class="responsive-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>Name</th>
                        <th>Description</th>
                        <th>Approval Limit Per Quarter</th>
                        <th class="text-end">Approval Amount Per Quarter</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="paginatedItems.length === 0">
                        <td colspan="6" class="text-center text-muted">No approval authorities to display.</td>
                      </tr>
                      <tr v-for="(approval_authority, index) in paginatedItems" :key="index"
                        :class="index % 2 === 0 ? 'row-even' : 'row-odd'">
                        <td data-label="S/N" class="serial-no">
                          <span>{{ (currentPage - 1) * pageLength + index + 1 }}</span>
                        </td>
                        <td data-label="Name" class="primary-col">
                          <router-link :to="{ name: 'UpdateApprovalAuthority', params: { id: approval_authority.id } }"
                            class="view-link text-decoration-underline">{{ approval_authority.name }}
                          </router-link>
                        </td>
                        <td data-label="Description" class="secondary-col">{{ approval_authority.description }}</td>
                        <td data-label="Approval Limit Per Quarter" class="secondary-col">
                          <span v-if="approval_authority.unlimited" class="unlimited-badge">Unlimited</span>
                          <span v-else>{{ approval_authority.limit_per_quarter }}</span>
                        </td>
                        <td data-label="Approval Amount Per Quarter" class="secondary-col amount">
                          <span v-if="approval_authority.unlimited" class="unlimited-badge">Unlimited</span>
                          <span v-else>{{ formatCurrency(approval_authority.approval_amt_per_qtr || 0).toLocaleString() }}</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Pagination Summary and Actions -->
              <div v-if="!loading" class="pagination-info">
                <small>
                  Showing {{ (currentPage - 1) * pageLength + 1 }} to
                  {{ Math.min(currentPage * pageLength, filteredItems.length) }} of
                  {{ filteredItems.length }} entries
                </small>

                <!-- Pagination Controls -->
                <nav class="pagination-nav">
                  <ul class="pagination pagination-sm mb-0">
                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                      <button class="page-link" @click.prevent="changePage(currentPage - 1)">Previous</button>
                    </li>
                    <li v-for="page in getPageNumbers" :key="page" class="page-item"
                      :class="{ active: currentPage === page, disabled: page === '...' }">
                      <button class="page-link" @click.prevent="page !== '...' && changePage(page)">{{ page }}</button>
                    </li>
                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                      <button class="page-link" @click.prevent="changePage(currentPage + 1)">Next</button>
                    </li>
                  </ul>
                </nav>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  </DashboardLayout>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import axios from 'axios'
import Swal from 'sweetalert2';
import Cookies from 'js-cookie';
import DashboardLayout from '../layout/DashboardLayout.vue';

import { debounce } from 'lodash'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import ExcelJS from 'exceljs'
import '@/assets/css/table.css'
import '@/assets/css/responsive-table.css' // Import global responsive styles

const approval_authorities = ref([])
const loading = ref(true)
const search = ref('')
const pageLength = ref(10)
const currentPage = ref(1)
const root_api_url = process.env.VUE_APP_API_ROOT_URL

const debouncedSearch = ref('')
watch(search, debounce((val) => {
  debouncedSearch.value = val
  currentPage.value = 1
}, 300))

watch(pageLength, () => {
  currentPage.value = 1
})

const fetchApprovalAuthorities = async () => {
  const token = Cookies.get('auth_token');
  loading.value = true
  try {
    const response = await axios.get(`${root_api_url}/eli/app-auth`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    approval_authorities.value = response.data.map(approval_authority => ({
      id: approval_authority.id,
      name: approval_authority.name,
      description: approval_authority.description,
      unique_position: approval_authority.uniquePositionInd,
      limit_per_quarter: approval_authority.limitPerQtr,
      approval_amt_per_qtr: approval_authority.approvalAmtPerQtr,
      unlimited: approval_authority.unlimitedApprovalInd === 1
    }))
  } catch (error) {
    console.error('Failed to fetch approval authorities:', error)
    const iconHtmlError = `<span class="iconify" data-icon="material-symbols:cancel-outline-rounded" style="font-size: 100px; color: red;"></span>`
    Swal.fire({
      html: `${iconHtmlError}
      <h1 style="font-weight:600;">Error</h1>
      <p>${error?.response?.data?.errorMessage || error?.response?.data?.error || error?.response?.data?.message || 'Failed to load approval authorities. Please try again.'}</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    });
  } finally {
    loading.value = false
  }
}

const filteredItems = computed(() => {
  return approval_authorities.value.filter(approval_authority => {
    const term = debouncedSearch.value.toLowerCase()
    return (
      approval_authority.name.toLowerCase().includes(term) ||
      approval_authority.description.toLowerCase().includes(term) ||
      approval_authority.unique_position.toLowerCase().includes(term) ||
      (approval_authority.unlimited ? 'unlimited' : approval_authority.limit_per_quarter.toString()).toLowerCase().includes(term) ||
      (approval_authority.unlimited ? 'unlimited' : approval_authority.approval_amt_per_qtr.toString()).toLowerCase().includes(term)
    )
  })
})

const paginatedItems = computed(() => {
  const start = (currentPage.value - 1) * pageLength.value
  return filteredItems.value.slice(start, start + pageLength.value)
})

const totalPages = computed(() => {
  return Math.ceil(filteredItems.value.length / pageLength.value)
})

const changePage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
  }
}

const getPageNumbers = computed(() => {
  const pages = [];
  const delta = 2;
  const total = totalPages.value;
  const current = currentPage.value;

  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
    return pages;
  }

  pages.push(1); // always show first

  if (current > delta + 2) pages.push('...');

  const start = Math.max(2, current - delta);
  const end = Math.min(total - 1, current + delta);

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (current < total - delta - 1) pages.push('...');

  pages.push(total); // always show last

  return pages;
});

const formatCurrency = (amount) => {
  return '₦' + amount.toLocaleString('en-NG')
}

const exportToPDF = () => {
  const doc = new jsPDF()
  autoTable(doc, {
    head: [['S/N', 'Name', 'Description', 'No. Of Unique Positions', 'Approval Limit Per Quarter', 'Approval Amount Per Quarter (₦)']],
    body: approval_authorities.value.map((approval_authority, index) => [
      index + 1,
      approval_authority.name,
      approval_authority.description,
      approval_authority.unique_position,
      approval_authority.unlimited ? 'Unlimited' : approval_authority.limit_per_quarter,
      approval_authority.unlimited ? 'Unlimited' : approval_authority.approval_amt_per_qtr
    ])
  })
  doc.save('approval_authorities.pdf')
}

const exportToExcel = async () => {
  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet('approval_authorities')
  worksheet.addRow(['S/N', 'Name', 'Description', 'No. Of Unique Positions', 'Approval Limit Per Quarter', 'Approval Amount Per Quarter (₦)'])

  approval_authorities.value.forEach((approval_authority, index) => {
    worksheet.addRow([
      index + 1,
      approval_authority.name,
      approval_authority.description,
      approval_authority.unique_position,
      approval_authority.unlimited ? 'Unlimited' : approval_authority.limit_per_quarter,
      approval_authority.unlimited ? 'Unlimited' : approval_authority.approval_amt_per_qtr
    ])
  })

  worksheet.getRow(1).font = { bold: true }

  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'approval_authorities.xlsx'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

onMounted(() => {
  fetchApprovalAuthorities()
  document.title = "View Approval Authorities"
});

</script>

<style scoped>
/* Fix horizontal scroll - override any parent overflow restrictions */
.view-item,
.form-inner,
.form-body {
  overflow: visible !important;
}

/* Component-specific styles */
.material-symbols-outlined {
  font-size: 1.25rem;
}

.primary-col {
  font-weight: 500;
}

.row-even {
  background-color: #ffffff;
}

.unlimited-badge {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  background-color: #e7f5ea;
  color: #1e7e34;
  font-weight: 600;
  font-size: 0.8rem;
}

.row-odd {
  background-color:var(--table-bg-head);
}
</style>
